import { type FormEvent, useState } from "react";
import type {
  ConfigSchema,
  HomeWidget,
  HomeWidgetDescriptor,
} from "../../api/hub/home";
import { HomePanel } from "./Frame";
const labels: Record<string, string> = {
  title: "部件标题",
  max: "每页显示条数",
  status: "任务范围",
  mode: "日历范围",
  period: "OKR 周期",
  projectIds: "关注项目编号",
  placeholder: "搜索提示",
  links: "快捷入口",
  label: "入口名称",
  href: "入口网址",
  text: "便签文字",
};
const optionLabels: Record<string, string> = {
  all: "全部",
  todo: "待办",
  in_progress: "进行中",
  in_review: "待验收",
  done: "已完成",
  overdue: "仅逾期",
  week: "本周",
  month: "本月",
};
function removeUndefined(
  value: Record<string, unknown>,
  key: string,
  next: unknown,
) {
  const result = { ...value };
  if (next === undefined) delete result[key];
  else result[key] = next;
  return result;
}
function SchemaField(
  { schema, value, set, name, required = false, depth = 0 }: {
    schema: ConfigSchema;
    value: unknown;
    set: (v: unknown) => void;
    name: string;
    required?: boolean;
    depth?: number;
  },
) {
  const type = Array.isArray(schema.type)
    ? schema.type.find((t) => t !== "null")
    : schema.type;
  const label = labels[name] ?? schema.description ?? name;
  if (depth > 8) {
    return <p className="home-error">配置嵌套过深，不能在网页编辑这个字段。</p>;
  }
  if (type === "object" || schema.properties) {
    const object = value && typeof value === "object" && !Array.isArray(value)
      ? value as Record<string, unknown>
      : {};
    return (
      <fieldset className="home-config-object">
        <legend>{label}</legend>
        {Object.entries(schema.properties ?? {}).map(([key, field]) => (
          <SchemaField
            key={key}
            name={key}
            schema={field}
            value={object[key]}
            set={(next) => set(removeUndefined(object, key, next))}
            required={schema.required?.includes(key)}
            depth={depth + 1}
          />
        ))}
      </fieldset>
    );
  }
  if (type === "array") {
    const array = Array.isArray(value) ? value : [];
    return (
      <fieldset className="home-config-array">
        <legend>{label}{required ? "（必填）" : ""}</legend>
        {schema.description && (
          <p className="home-muted">{schema.description}</p>
        )}
        {array.map((item, i) => (
          <div key={i} className="home-array-item">
            <SchemaField
              name={name + " " + (i + 1)}
              schema={schema.items ?? { type: "string" }}
              value={item}
              set={(next) => set(array.map((v, at) => at === i ? next : v))}
              required
              depth={depth + 1}
            />
            <button
              type="button"
              onClick={() => set(array.filter((_, at) => at !== i))}
            >
              删除第 {i + 1} 项
            </button>
          </div>
        ))}
        <button
          type="button"
          disabled={schema.maxItems !== undefined &&
            array.length >= schema.maxItems}
          onClick={() =>
            set([
              ...array,
              schema.items?.type === "object"
                ? {}
                : schema.items?.type === "number" ||
                    schema.items?.type === "integer"
                ? 0
                : "",
            ])}
        >
          添加一项
        </button>
        <p className="home-muted">
          {schema.maxItems !== undefined
            ? "最多 " + schema.maxItems + " 项。"
            : ""}保存后仍由服务端校验权限与链接白名单。
        </p>
      </fieldset>
    );
  }
  if (schema.enum) {
    return (
      <label className="home-field">
        <span>{label}{required ? "（必填）" : ""}</span>
        <select
          aria-label={label}
          value={value === undefined ? "" : String(value)}
          onChange={(e) =>
            set(
              e.target.value === ""
                ? undefined
                : schema.enum?.find((v) => String(v) === e.target.value),
            )}
          required={required}
        >
          <option value="">使用默认</option>
          {schema.enum.map((option, i) => (
            <option value={String(option)} key={i}>
              {optionLabels[String(option)] ?? String(option)}
            </option>
          ))}
        </select>
      </label>
    );
  }
  if (type === "boolean") {
    return (
      <label className="home-check">
        <input
          type="checkbox"
          checked={value === true}
          onChange={(e) => set(e.target.checked)}
        />
        {label}
      </label>
    );
  }
  if (type === "number" || type === "integer") {
    return (
      <label className="home-field">
        <span>{label}{required ? "（必填）" : ""}</span>
        <input
          aria-label={label}
          type="number"
          min={schema.minimum}
          max={schema.maximum}
          step={type === "integer" ? 1 : "any"}
          value={typeof value === "number" ? value : ""}
          required={required}
          onChange={(e) =>
            set(e.target.value === "" ? undefined : Number(e.target.value))}
        />
        <span className="home-muted">
          {schema.minimum ?? "不限"} 至 {schema.maximum ?? "不限"}
        </span>
      </label>
    );
  }
  if (type === "string" || !type) {
    return (
      <label className="home-field">
        <span>{label}{required ? "（必填）" : ""}</span>
        <textarea
          aria-label={label}
          value={typeof value === "string" ? value : ""}
          required={required}
          maxLength={schema.maxLength}
          minLength={required ? schema.minLength : undefined}
          onChange={(e) =>
            set(
              e.target.value === "" && !required ? undefined : e.target.value,
            )}
          rows={name === "text" ? 5 : 2}
        />
        {schema.maxLength !== undefined && (
          <span className="home-muted">
            最多 {schema.maxLength} 字 · 纯文本
          </span>
        )}
      </label>
    );
  }
  return (
    <p className="home-error">
      {label} 的格式暂不支持网页编辑，请通过已授权的 AI 配置。
    </p>
  );
}
function configError(
  value: unknown,
  schema: ConfigSchema,
  path = "配置",
): string | null {
  if (value === undefined) return null;
  if (
    schema.enum &&
    !schema.enum.some((option) =>
      JSON.stringify(option) === JSON.stringify(value)
    )
  ) return path + "必须选择提供的选项";
  const type = Array.isArray(schema.type)
    ? schema.type.find((t) => t !== "null")
    : schema.type;
  if (type === "object") {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return path + "必须是对象";
    }
    const record = value as Record<string, unknown>;
    for (const key of schema.required ?? []) {
      if (record[key] === undefined) return (labels[key] ?? key) + "是必填项";
    }
    for (const [key, item] of Object.entries(record)) {
      if (schema.additionalProperties === false && !schema.properties?.[key]) {
        return "请移除不允许的字段：" + key;
      }
      const error = configError(
        item,
        schema.properties?.[key] ?? {},
        labels[key] ?? key,
      );
      if (error) return error;
    }
  }
  if (type === "array") {
    if (!Array.isArray(value)) return path + "必须是列表";
    if (schema.maxItems !== undefined && value.length > schema.maxItems) {
      return path + "最多 " + schema.maxItems + " 项";
    }
    if (schema.minItems !== undefined && value.length < schema.minItems) {
      return path + "至少 " + schema.minItems + " 项";
    }
    for (const item of value) {
      const error = configError(item, schema.items ?? {}, path);
      if (error) return error;
    }
  }
  if (type === "string") {
    if (typeof value !== "string") return path + "必须是纯文本";
    if (/<\/?[A-Za-z!]|(?:javascript|vbscript|data)\s*:/i.test(value)) {
      return path + "只能写纯文本，不能放 HTML 或脚本";
    }
    if (schema.minLength !== undefined && value.length < schema.minLength) {
      return path + "至少 " + schema.minLength + " 字";
    }
    if (schema.maxLength !== undefined && value.length > schema.maxLength) {
      return path + "最多 " + schema.maxLength + " 字";
    }
    if (schema.pattern) {
      try {
        if (!new RegExp(schema.pattern).test(value)) return path + "格式不正确";
      } catch {
        return "字段校验规则无效，请联系管理员";
      }
    }
  }
  if (type === "number" || type === "integer") {
    if (
      typeof value !== "number" || !Number.isFinite(value) ||
      type === "integer" && !Number.isInteger(value)
    ) return path + "需要有效" + (type === "integer" ? "整数" : "数字");
    if (schema.minimum !== undefined && value < schema.minimum) {
      return path + "不能小于 " + schema.minimum;
    }
    if (schema.maximum !== undefined && value > schema.maximum) {
      return path + "不能大于 " + schema.maximum;
    }
  }
  return null;
}
export function ConfigPanel(
  { widget, descriptor, onApply, onClose }: {
    widget: HomeWidget;
    descriptor: HomeWidgetDescriptor;
    onApply: (config: Record<string, unknown>) => void;
    onClose: () => void;
  },
) {
  const [config, setConfig] = useState<Record<string, unknown>>(() =>
      JSON.parse(JSON.stringify(widget.config))
    ),
    [error, setError] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const problem = configError(config, descriptor.configSchema);
    if (problem) {
      setError(problem);
      return;
    }
    onApply(config);
    onClose();
  };
  return (
    <HomePanel title={"配置 · " + descriptor.name} onClose={onClose}>
      <p className="home-muted">
        {descriptor.description}。修改只应用到草稿，保存主页后生效。
      </p>
      <form onSubmit={submit} className="home-config-form">
        {Object.entries(descriptor.configSchema.properties ?? {}).map((
          [key, schema],
        ) => (
          <SchemaField
            key={key}
            name={key}
            schema={schema}
            value={config[key]}
            set={(value) =>
              setConfig((current) => removeUndefined(current, key, value))}
            required={descriptor.configSchema.required?.includes(key)}
          />
        ))}
        {!Object.keys(descriptor.configSchema.properties ?? {}).length && (
          <p>这个部件没有网页可配置的字段。</p>
        )}
        {error && <p className="home-error" role="alert">{error}</p>}
        <button type="submit" className="home-primary">应用到草稿</button>
      </form>
    </HomePanel>
  );
}
